import { useEffect, useRef } from 'react';
import {
  cartItemFor, dayKeysOf, dayOrder, findDay, findTalent, slotsFor, tiersFor,
  type Action, type AppState, type TenantBundle,
} from '@ce/core';
import type { Dispatch } from 'react';
import { btn, divider, kicker, pill, progressDot, t } from '../styles';
import { CardArc } from './Chrome';
import { Sheen } from './Ambient';

const STEP_TITLES = ['Choose what you want', 'Pick a time slot', 'Review', 'Booked'];

export function BookingSheet({
  bundle,
  state,
  dispatch,
}: {
  bundle: TenantBundle;
  state: AppState;
  dispatch: Dispatch<Action>;
}) {
  const booking = state.booking;
  const sheetRef = useRef<HTMLDivElement>(null);

  // Escape closes; focus moves into the sheet so keyboard users are not stranded
  // behind it.
  useEffect(() => {
    if (!booking) return;
    sheetRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch({ type: 'closeBooking' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [booking, dispatch]);

  if (!booking) return null;

  const person = findTalent(bundle, booking.talentId);
  const tiers = tiersFor(person);
  const tier = tiers.find((x) => x.key === booking.tier) ?? tiers[0];
  const availableDays = dayKeysOf(person.days, dayOrder(bundle));
  const activeDay = availableDays.includes(booking.day) ? booking.day : availableDays[0];
  const dayLabel = findDay(bundle, activeDay).label;
  const slots = slotsFor(bundle);
  const cur = bundle.event.currency;

  const nextLabels = [
    'Choose a time',
    booking.slot ? 'Review booking' : 'Pick a slot first',
    'Add to cart',
    'View my vouchers',
  ];

  const advance = () => {
    if (booking.step === 1 && !booking.slot) return;
    if (booking.step === 2) {
      dispatch({ type: 'bookingAddToCart', item: cartItemFor(bundle, { ...booking, day: activeDay }) });
      return;
    }
    if (booking.step === 3) {
      dispatch({ type: 'closeBooking' });
      dispatch({ type: 'go', screen: 'cart' });
      return;
    }
    dispatch({ type: 'bookingStep', step: (booking.step + 1) as 0 | 1 | 2 | 3 });
  };

  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 50, display: 'flex', alignItems: 'flex-end',
        background: 'rgba(15,16,26,0.68)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) dispatch({ type: 'closeBooking' });
      }}
    >
      <div
        ref={sheetRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`Book with ${person.name}`}
        style={{
          width: '100%', maxHeight: '88%', display: 'flex', flexDirection: 'column',
          borderRadius: '18px 18px 0 0', background: 'var(--color-surface)',
          boxShadow: 'var(--shadow-lg)', animation: 'sheet .28s cubic-bezier(.2,.8,.2,1) both',
          overflow: 'hidden', outline: 'none',
        }}
      >
        <div style={{ flex: 'none', padding: '16px 20px 12px', position: 'relative' }}>
          <Sheen delay={0.8} duration={10} />
          <CardArc size={200} top={-80} right={-60} opacity={0.14} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={kicker(9.5, 'var(--color-accent-300)', 0.16)}>
                {booking.step === 3 ? 'Confirmed' : `${person.name} · step ${booking.step + 1} of 3`}
              </div>
              <div
                style={{
                  font: '500 19px/1.2 var(--font-body)', marginTop: 8, letterSpacing: '-0.015em',
                }}
              >
                {STEP_TITLES[booking.step]}
              </div>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => dispatch({ type: 'closeBooking' })}
              style={{
                flex: 'none', width: 32, height: 32, borderRadius: 8,
                border: `1px solid ${divider}`, background: 'transparent', color: t(0.7),
                cursor: 'pointer', fontSize: 15, lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 14 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={progressDot(i <= booking.step)} />
            ))}
          </div>
        </div>

        <div className="scroll" style={{ flex: 1, overflow: 'auto', padding: '4px 20px 0' }}>
          {booking.step === 0 && (
            <div style={{ display: 'grid', gap: 9 }}>
              {tiers.map((x) => {
                const on = booking.tier === x.key;
                return (
                  <button
                    key={x.key}
                    type="button"
                    onClick={() => dispatch({ type: 'bookingTier', tier: x.key })}
                    aria-pressed={on}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 13, width: '100%', padding: 14,
                      borderRadius: 10, cursor: 'pointer', color: 'var(--color-text)',
                      border: `1px solid ${on ? 'var(--color-accent)' : divider}`,
                      background: on ? 'rgba(var(--color-accent-rgb), 0.14)' : 'transparent',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <div style={{ font: '500 14.5px/1.2 var(--font-body)' }}>{x.label}</div>
                      <div style={{ fontSize: 11.5, lineHeight: 1.45, color: t(0.5), marginTop: 4 }}>
                        {x.note}
                      </div>
                    </div>
                    <div style={{ font: '500 16px/1 var(--font-body)', flex: 'none' }}>
                      {cur}{x.price}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {booking.step === 1 && (
            <div>
              <div style={{ display: 'flex', gap: 7, marginBottom: 14 }}>
                {availableDays.map((key) => {
                  const d = findDay(bundle, key);
                  const label = d.dow.charAt(0) + d.dow.slice(1).toLowerCase();
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => dispatch({ type: 'bookingDay', day: key })}
                      aria-pressed={activeDay === key}
                      style={pill(activeDay === key)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {slots.map((slot) => {
                  const on = booking.slot === slot.time;
                  return (
                    <button
                      key={slot.time}
                      type="button"
                      disabled={slot.soldOut}
                      onClick={() => dispatch({ type: 'bookingSlot', slot: slot.time })}
                      style={{
                        padding: '11px 0', borderRadius: 9,
                        cursor: slot.soldOut ? 'not-allowed' : 'pointer',
                        opacity: slot.soldOut ? 0.4 : 1,
                        border: `1px solid ${on ? 'var(--color-accent)' : divider}`,
                        background: on ? 'rgba(var(--color-accent-rgb), 0.18)' : 'transparent',
                        color: on ? 'var(--color-accent-300)' : 'var(--color-text)',
                      }}
                    >
                      <div style={{ font: '500 14px/1 var(--font-body)' }}>{slot.time}</div>
                      <div style={{ fontSize: 10, marginTop: 4, opacity: 0.6 }}>{slot.left}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11.5, lineHeight: 1.55, color: t(0.42), marginTop: 13 }}>
                {bundle.strings.bookingSlotNote}
              </div>
            </div>
          )}

          {booking.step === 2 && (
            <div>
              <div
                style={{
                  padding: 15, borderRadius: 10, background: 'var(--color-accent-900)',
                  border: '1px solid var(--color-accent-700)',
                }}
              >
                {[
                  { label: 'Talent', value: person.name },
                  { label: 'Type', value: tier.label },
                  { label: 'When', value: `${dayLabel} · ${booking.slot ?? '—'}` },
                  { label: 'Where', value: tier.room },
                  { label: 'Price', value: `${cur}${tier.price}` },
                ].map((r) => (
                  <div
                    key={r.label}
                    style={{
                      display: 'flex', justifyContent: 'space-between', gap: 14,
                      padding: '5px 0', fontSize: 12.5,
                    }}
                  >
                    <span style={{ color: t(0.55) }}>{r.label}</span>
                    <span style={{ fontWeight: 500, textAlign: 'right' }}>{r.value}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11.5, lineHeight: 1.6, color: t(0.45), marginTop: 13 }}>
                {bundle.strings.bookingReviewNote}
              </div>
            </div>
          )}

          {booking.step === 3 && (
            <div style={{ textAlign: 'center', padding: '8px 0 0' }}>
              <div
                style={{
                  width: 108, height: 108, margin: '0 auto', borderRadius: 10,
                  // Fixed contrast, not themed: a reader needs black on white
                  // whatever look the attendee picked.
                  background: 'repeating-linear-gradient(45deg, #e9e9ed 0 4px, #16172a 4px 8px)',
                }}
              />
              <div style={{ font: '500 17px/1.3 var(--font-body)', margin: '16px 0 6px' }}>
                Slot held and added to cart
              </div>
              <div
                style={{
                  fontSize: 12.5, lineHeight: 1.6, color: t(0.55), maxWidth: 280, margin: '0 auto',
                }}
              >
                {tier.label} with {person.name}, {dayLabel} at {booking.slot}. Pay in the cart within
                10 minutes to keep the slot.
              </div>
              <div
                style={{
                  font: '400 11px/1 var(--font-mono)', color: 'var(--color-accent-300)', marginTop: 12,
                }}
              >
                HOLD · NC26-{tier.label.slice(0, 2).toUpperCase()}-{(booking.slot ?? '').replace(':', '')}
              </div>
            </div>
          )}
          <div style={{ height: 16 }} />
        </div>

        <div
          style={{
            flex: 'none', padding: '12px 20px 20px', display: 'flex', gap: 9,
            alignItems: 'center', borderTop: `1px solid ${divider}`,
          }}
        >
          <button
            type="button"
            onClick={() =>
              booking.step === 0
                ? dispatch({ type: 'closeBooking' })
                : dispatch({ type: 'bookingStep', step: (booking.step - 1) as 0 | 1 | 2 | 3 })
            }
            style={btn('ghost', { fontSize: 12.5 })}
          >
            {booking.step === 0 ? 'Cancel' : 'Back'}
          </button>
          <button
            type="button"
            onClick={advance}
            style={btn('primary', { flex: 1, height: 46, fontSize: 14.5, borderRadius: 10 })}
          >
            {nextLabels[booking.step]}
          </button>
        </div>
      </div>
    </div>
  );
}
